import React from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const colors = [
    '#FF6B6B',  // Bright Red - Ahemdabad
    '#4ECDC4',  // Turquoise - Banglore
    '#45B7D1',  // Sky Blue - Delhi
    '#96CEB4',  // Mint - Hyderabad
    '#FFEEAD',  // Cream Yellow - Indore
    '#D4A5A5',  // Dusty Rose - Mumbai
    '#9B59B6',  // Purple - Nagpur
    '#3498DB',  // Blue - Pune
    '#E67E22',  // Orange - Raipur
    '#2ECC71',  // Emerald Green - Surat
    '#F1C40F',  // Yellow - Vadodara
];

const branches = [
    "Ahemdabad",
    "Banglore",
    "Delhi",
    "Hyderabad",
    "Indore",
    "Mumbai",
    "Nagpur",
    "Pune",
    "Raipur",
    "Surat",
    "Vadodara",
];

const BranchMonthlyPatientsChart = ({ data }) => {
    const months = Object.keys(data);

    const datasets = branches.map((branch, index) => ({
        label: branch,
        data: months.map((month) => data[month][branch] || 0),
        backgroundColor: colors[index % colors.length],
        borderColor: colors[index % colors.length],
        borderWidth: 1,
        borderRadius: 1,
        borderSkipped: false,
        barThickness: 8, // Much thinner bars
        maxBarThickness: 10,
    }));

    const chartData = {
        labels: months,
        datasets,
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 14,
                    boxHeight: 14,
                    padding: 16,
                    usePointStyle: true,
                    pointStyle: 'rect',
                    font: {
                        size: 13,
                        weight: '500'
                    },
                },
                onClick: (e, legendItem, legend) => {
                    const index = legendItem.datasetIndex;
                    const ci = legend.chart;
                    if (ci.isDatasetVisible(index)) {
                        ci.hide(index);
                        legendItem.hidden = true;
                    } else {
                        ci.show(index);
                        legendItem.hidden = false;
                    }
                }
            },
            title: {
                display: true,
                text: "Branch-wise Monthly Patient Count",
                font: {
                    size: 18,
                    weight: 'bold'
                },
                color: '#1F2937',
                padding: {
                    top: 10,
                    bottom: 30,
                },
            },
            tooltip: {
                mode: "index",
                intersect: false,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1F2937',
                bodyColor: '#374151',
                borderColor: '#E5E7EB',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                displayColors: true,
                callbacks: {
                    title: function (context) {
                        return `Month: ${context[0].label}`;
                    },
                    label: function (context) {
                        return `${context.dataset.label}: ${context.parsed.y} patients`;
                    }
                }
            },
        },
        scales: {
            x: {
                categoryPercentage: 0.9, // Increase spacing between month groups
                barPercentage: 0.8, // Reduce bar thickness within each group
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 13,
                        weight: '500'
                    },
                    color: '#374151',
                    padding: 8,
                },
                border: {
                    display: false,
                }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 10,
                    font: {
                        size: 12,
                    },
                    color: '#6B7280',
                    padding: 8,
                    callback: function (value) {
                        return value;
                    }
                },
                grid: {
                    drawBorder: false,
                    color: '#F3F4F6',
                    lineWidth: 1,
                },
                border: {
                    display: false,
                }
            },
        },
        elements: {
            bar: {
                borderWidth: 0,
            }
        },
        layout: {
            padding: {
                top: 10,
                bottom: 10,
                left: 10,
                right: 10,
            }
        }
    };

    return (
        <div className="w-full bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="p-6">
                <div className="h-96 w-full">
                    <Bar options={options} data={chartData} />
                </div>
            </div>
            <div className="px-6 pb-4">
                <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                        Total Branches: {branches.length}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-50 text-green-700">
                        Months Tracked: {months.length}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default BranchMonthlyPatientsChart;